import React, { useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '~/tools/makeStyles';

import { mui } from '~/tools/mui';
import { layout } from '~/tools/layout';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontSize: layout.pxToRem(22),
    fontWeight: 400,
  },
  dialogContent: {
    fontSize: layout.pxToRem(16),
    fontWeight: 500,
  },
  dialogContentSpacing: {
    marginBottom: theme.spacing(3),
  },
  dialogActions: {
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  reasonLabel: {
    color: theme.palette.common.black,
    fontSize: layout.pxToRem(16),
    fontWeight: 700,
    marginTop: layout.pxToRem(16),
    marginBottom: layout.pxToRem(12),
  },
  reasonLabelRequired: {
    color: theme.palette.error.main,
  },
}));

type ConfirmActionDialogProps = {
  isOpen: boolean;
  title: string;
  descriptions: string[];
  confirmButtonText: string;
  cancelButtonText: string;
  messageRequired?: {
    label: string;
    placeholder: string;
  };
  onClose: () => void;
  handleConfirmClick: (message?: string) => void;
  handleCancelClick: () => void;
};

const ConfirmActionDialog = ({
  isOpen,
  title,
  descriptions,
  confirmButtonText,
  cancelButtonText,
  messageRequired,
  onClose,
  handleConfirmClick,
  handleCancelClick,
}: ConfirmActionDialogProps) => {
  const classes = useStyles();
  const [message, setMessage] = useState<string>('');

  const handleMessageOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newMessage = event.target.value;
    setMessage(newMessage);
  };

  return (
    <mui.Dialog
      data-testid="confirm-action-dialog"
      open={isOpen}
      onBackdropClick={onClose}
      onClose={onClose}
      fullWidth={true}
      maxWidth="sm"
    >
      <mui.DialogTitle>
        <mui.Typography data-testid="confirm-action-dialog-title" className={classes.dialogTitle}>
          {title}
        </mui.Typography>
      </mui.DialogTitle>
      <mui.DialogContent>
        {descriptions?.map((description, index) => (
          <mui.Typography
            key={index}
            data-testid={`confirm-action-dialog-description-${index}`}
            variant="body1"
            className={classNames(classes.dialogContent, {
              [classes.dialogContentSpacing]: index !== descriptions.length - 1,
            })}
          >
            {description}
          </mui.Typography>
        ))}
        {!!messageRequired && (
          <mui.InputLabel>
            <mui.Typography id="reason-label" className={classes.reasonLabel}>
              {messageRequired.label}
              <span className={classes.reasonLabelRequired}>*</span>
            </mui.Typography>
            <mui.TextField
              value={message}
              placeholder={messageRequired.placeholder}
              aria-labelledby="reason-label"
              variant="outlined"
              fullWidth
              onChange={handleMessageOnChange}
            ></mui.TextField>
          </mui.InputLabel>
        )}
      </mui.DialogContent>
      <mui.DialogActions className={classes.dialogActions}>
        <mui.Button
          data-testid="confirm-action-dialog-confirm-button"
          color="primary"
          variant="contained"
          disabled={messageRequired && !message}
          onClick={() => handleConfirmClick(message)}
        >
          {confirmButtonText.toUpperCase()}
        </mui.Button>
        <mui.Button onClick={handleCancelClick} color="primary" data-testid="confirm-action-dialog-cancel-button">
          {cancelButtonText.toUpperCase()}
        </mui.Button>
      </mui.DialogActions>
    </mui.Dialog>
  );
};

export { ConfirmActionDialog };
